/**
 * LocationSpinController
 * Generic drag-to-rotate controller for any focused location model.
 *
 * Uses quaternion-based arcball (trackball) rotation so that drag direction
 * always matches the visual result regardless of the model's current orientation.
 * Dragging right always rotates the visible face to the right; dragging up always
 * tilts it upward — even when the model is flipped or rotated far from its rest pose.
 *
 * Rotation axes are derived from the camera's world-space right and up vectors each
 * frame, so the behaviour is always relative to what the viewer sees.
 *
 * Inertia coast after release uses the same camera-relative axis stored at the
 * moment the pointer is lifted, so the spin continues in the correct direction.
 *
 * Spring return goes back to the initialRotation quaternion (not identity) so
 * models always return to their configured rest pose.
 *
 * Props:
 *   groupRef        — ref to the Three.js Group to rotate
 *   isFocused       — boolean; true while this location is in focus
 *   initialRotation — [x, y, z] Euler angles (radians) for the rest pose; defaults [0,0,0]
 */

import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Euler, Quaternion, Vector3 } from 'three'

const DRAG_SENSITIVITY   = 0.005  // radians per pixel
const FRICTION           = 0.015  // velocity fraction remaining after 1s (lower = longer coast)
const RETURN_SMOOTH_TIME = 2.0    // seconds to spring back to rest pose after unfocus
const VELOCITY_WINDOW    = 120    // ms of pointer history used for release velocity

export function LocationSpinController({ groupRef, isFocused, initialRotation = [0, 0, 0] }) {
  const { gl, camera } = useThree()

  // Current orientation as a quaternion — the source of truth
  const quatRef = useRef(new Quaternion())

  // Rest-pose quaternion derived from initialRotation prop
  const restQuatRef = useRef(new Quaternion())

  // Angular velocity for inertia coast: { axis: Vector3, speed: number (rad/s) }
  const angVelRef = useRef({ axis: new Vector3(0, 1, 0), speed: 0 })

  const isDraggingRef = useRef(false)
  const returningRef  = useRef(false)
  const lastPosRef    = useRef({ x: 0, y: 0 })

  // Rolling buffer of { t, dx, dy } — used to compute release velocity
  const samplesRef = useRef([])

  // Recompute restQuat whenever initialRotation changes
  useEffect(() => {
    const euler = new Euler(...initialRotation, 'XYZ')
    restQuatRef.current.setFromEuler(euler)
    // If not currently in a user interaction, also snap current quat to rest pose
    // (so the model starts at the right orientation on first focus)
    if (!isFocused && !returningRef.current) {
      quatRef.current.copy(restQuatRef.current)
      if (groupRef.current) {
        groupRef.current.quaternion.copy(quatRef.current)
      }
    }
  }, [initialRotation[0], initialRotation[1], initialRotation[2]]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFocused) {
      isDraggingRef.current     = false
      samplesRef.current        = []
      angVelRef.current.speed   = 0
      returningRef.current      = true
      return
    }

    returningRef.current = false

    const canvas = gl.domElement

    const onPointerDown = (e) => {
      isDraggingRef.current       = true
      lastPosRef.current          = { x: e.clientX, y: e.clientY }
      samplesRef.current          = []
      angVelRef.current.speed     = 0
      canvas.setPointerCapture(e.pointerId)
    }

    const onPointerMove = (e) => {
      if (!isDraggingRef.current) return
      const now = performance.now()
      const dx  = e.clientX - lastPosRef.current.x
      const dy  = e.clientY - lastPosRef.current.y
      lastPosRef.current = { x: e.clientX, y: e.clientY }

      // Apply an immediate arcball rotation using camera-relative axes
      applyArcballDelta(quatRef.current, camera, dx, dy, DRAG_SENSITIVITY)

      // Push to rolling buffer and prune old samples
      samplesRef.current.push({ t: now, dx, dy })
      const cutoff = now - VELOCITY_WINDOW
      samplesRef.current = samplesRef.current.filter(s => s.t >= cutoff)
    }

    const onPointerUp = (e) => {
      isDraggingRef.current = false
      canvas.releasePointerCapture(e.pointerId)

      // Compute release velocity from the rolling window
      const samples = samplesRef.current
      if (samples.length >= 2) {
        const dt = (samples[samples.length - 1].t - samples[0].t) / 1000
        if (dt > 0) {
          const totalDx = samples.reduce((s, p) => s + p.dx, 0)
          const totalDy = samples.reduce((s, p) => s + p.dy, 0)

          // Build a combined camera-relative axis for the accumulated drag
          const axis = getCameraRelativeAxis(camera, totalDx, totalDy)
          const speed = (Math.sqrt(totalDx * totalDx + totalDy * totalDy) * DRAG_SENSITIVITY) / dt
          angVelRef.current.axis.copy(axis)
          angVelRef.current.speed = speed
        }
      }
      samplesRef.current = []
    }

    canvas.addEventListener('pointerdown',   onPointerDown)
    canvas.addEventListener('pointermove',   onPointerMove)
    canvas.addEventListener('pointerup',     onPointerUp)
    canvas.addEventListener('pointercancel', onPointerUp)

    return () => {
      canvas.removeEventListener('pointerdown',   onPointerDown)
      canvas.removeEventListener('pointermove',   onPointerMove)
      canvas.removeEventListener('pointerup',     onPointerUp)
      canvas.removeEventListener('pointercancel', onPointerUp)
      isDraggingRef.current = false
    }
  }, [isFocused, gl]) // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, delta) => {
    if (!groupRef.current) return

    if (returningRef.current) {
      // Smooth slerp back to rest pose
      const t = 1 - Math.pow(2, -delta / RETURN_SMOOTH_TIME)
      quatRef.current.slerp(restQuatRef.current, t)

      const angleDiff = quatRef.current.angleTo(restQuatRef.current)
      if (angleDiff < 0.0005) {
        quatRef.current.copy(restQuatRef.current)
        returningRef.current = false
      }

      groupRef.current.quaternion.copy(quatRef.current)
      return
    }

    if (!isFocused) return

    // Coast: friction decay on angular speed, applied only when not dragging
    if (!isDraggingRef.current) {
      const friction = Math.pow(FRICTION, delta)
      angVelRef.current.speed *= friction

      if (angVelRef.current.speed > 0.0001) {
        const angle = angVelRef.current.speed * delta
        const dq = _tempQuat.setFromAxisAngle(angVelRef.current.axis, angle)
        quatRef.current.premultiply(dq)
      }
    }

    groupRef.current.quaternion.copy(quatRef.current)
  })

  return null
}

// ---------------------------------------------------------------------------
// Helpers (module-level, no allocations per frame)
// ---------------------------------------------------------------------------

const _tempQuat  = new Quaternion()
const _rightVec  = new Vector3()
const _upVec     = new Vector3()
const _axis      = new Vector3()
const _dqX       = new Quaternion()
const _dqY       = new Quaternion()

/**
 * Apply an arcball delta to `quat` using the camera's world-space right and up
 * vectors as rotation axes so the rotation always matches drag direction.
 *
 * dx > 0  → drag right  → rotate around camera up   (positive angle)
 * dy > 0  → drag down   → rotate around camera right (positive angle)
 */
function applyArcballDelta(quat, camera, dx, dy, sensitivity) {
  // Camera right and up in world space
  camera.matrixWorld.extractBasis(_rightVec, _upVec, _axis /* ignored */)
  _rightVec.normalize()
  _upVec.normalize()

  const angleY = dx * sensitivity  // horizontal drag → rotate around camera up
  const angleX = dy * sensitivity  // vertical drag   → rotate around camera right

  _dqY.setFromAxisAngle(_upVec,   angleY)
  _dqX.setFromAxisAngle(_rightVec, angleX)

  // Premultiply so the rotation is applied in world space (camera-relative)
  quat.premultiply(_dqY).premultiply(_dqX)
}

/**
 * Return the normalised world-space rotation axis for a (dx, dy) drag delta
 * relative to the camera. Used to store inertia axis on pointer-up.
 */
function getCameraRelativeAxis(camera, dx, dy) {
  camera.matrixWorld.extractBasis(_rightVec, _upVec, _axis /* ignored */)
  _rightVec.normalize()
  _upVec.normalize()

  // Combined axis: up-component from horizontal drag, right-component from vertical drag
  const ax = _upVec.clone().multiplyScalar(dx)
  const ay = _rightVec.clone().multiplyScalar(dy)
  return ax.add(ay).normalize()
}
